import { useState, useEffect, useRef } from 'react'
import Layout from '../../../../components/vision/Layout'
import ProtectedRoute from '../../../../components/vision/ProtectedRoute'
import { useAuth } from '../../../../contexts/AuthContext'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts'

interface Mensagem {
  role: 'user' | 'assistant'
  content: string
  dados_calculados?: any
  timestamp: string
}

interface Conversa {
  id: string
  cliente_id: string
  periodo_referencia: string
  titulo: string
  criado_em: string
  atualizado_em: string
  tokens_usados: number
  custo_estimado: number
}

export default function PrevisibilidadeIAPage() {
  const { user } = useAuth()
  const [clientes, setClientes] = useState<any[]>([])
  const [selectedCliente, setSelectedCliente] = useState<any>(null)
  const [periodoReferencia, setPeriodoReferencia] = useState<string>('')
  const [mensagens, setMensagens] = useState<Mensagem[]>([])
  const [inputMensagem, setInputMensagem] = useState('')
  const [loading, setLoading] = useState(false)
  const [loadingClientes, setLoadingClientes] = useState(true)
  const [conversaAtual, setConversaAtual] = useState<string | null>(null)
  const [historico, setHistorico] = useState<Conversa[]>([])
  const [showHistorico, setShowHistorico] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [showClientesList, setShowClientesList] = useState(false)
  const [dadosPeriodo, setDadosPeriodo] = useState<any>(null)
  const [loadingDados, setLoadingDados] = useState(false)
  const [showModalPlanejamento, setShowModalPlanejamento] = useState(false)
  const [dadosParaSalvar, setDadosParaSalvar] = useState<any>(null)
  const [periodoDestino, setPeriodoDestino] = useState<string>('')
  const [salvandoPlanejamento, setSalvandoPlanejamento] = useState(false)
  const [valoresEditaveis, setValoresEditaveis] = useState<any>({})
  const chatEndRef = useRef<HTMLDivElement>(null)
  const [modoBase, setModoBase] = useState<'mes' | 'media3' | 'media6' | 'melhor12'>('mes')
  const [descricaoBase, setDescricaoBase] = useState<string>('')
  const [kpisOverride, setKpisOverride] = useState<any>(null)

  useEffect(() => {
    fetchClientes()
    const hoje = new Date()
    const mesAnterior = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1)
    const mesInicial = `${mesAnterior.getFullYear()}-${String(mesAnterior.getMonth() + 1).padStart(2, '0')}`
    setPeriodoReferencia(mesInicial)
  }, [])

  useEffect(() => {
    if (selectedCliente) {
      fetchHistorico()
    }
  }, [selectedCliente])

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [mensagens])

  useEffect(() => {
    if (selectedCliente && periodoReferencia) {
      fetchDadosModoAtivo()
    }
  }, [selectedCliente, periodoReferencia, modoBase])

  const fetchDadosPeriodo = async () => {
    if (!selectedCliente || !periodoReferencia) return

    setLoadingDados(true)
    try {
      const params = new URLSearchParams({
        cliente_id: selectedCliente.id,
        competencia1: periodoReferencia
      })

      const res = await fetch(`/api/gestao/kpis/dashboard-resultados?${params}`)
      if (res.ok) {
        const data = await res.json()
        setDadosPeriodo(data)
      }
    } catch (error) {
      console.error('Erro ao buscar dados do período:', error)
    }
    setLoadingDados(false)
  }

  const calcularMesesAnteriores = (mesRef: string, n: number): string[] => {
    const [ano, mes] = mesRef.split('-').map(Number)
    const meses: string[] = []
    for (let i = n - 1; i >= 0; i--) {
      const data = new Date(ano, mes - 1 - i, 1)
      meses.push(`${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`)
    }
    return meses
  }

  const formatarMesLabel = (mesStr: string): string => {
    const [ano, mes] = mesStr.split('-')
    const nomes = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
    return `${nomes[parseInt(mes) - 1]}/${ano.slice(2)}`
  }

  const extrairKPIsDosPeriodo = (indicadores: any[]): any => {
    const find = (...nomes: string[]) => {
      for (const nome of nomes) {
        const ind = indicadores.find((i: any) => i.nome.toLowerCase().includes(nome.toLowerCase()))
        if (ind) return parseFloat(ind.valor1) || 0
      }
      return 0
    }
    return {
      investimento_midias: find('investimento em m\u00eddias', 'investimento'),
      leads_gerados: find('leads gerados', 'leads gerado'),
      taxa_agendamento: find('taxa lead\u2192agendado', 'agendamento'),
      taxa_comparecimento: find('taxa agendado\u2192comparecimento', 'comparecimento'),
      taxa_fechamento: find('taxa comparecimento\u2192venda', 'fechamento'),
      ticket_medio: find('ticket m\u00e9dio', 'ticket medio'),
      faturamento: find('total faturado', 'faturamento'),
      roi: find('roi', 'retorno'),
      cac: find('cac', 'custo de aquisi\u00e7\u00e3o')
    }
  }

  const fetchDadosMediaMeses = async (n: number) => {
    if (!selectedCliente || !periodoReferencia) return
    setLoadingDados(true)
    try {
      const meses = calcularMesesAnteriores(periodoReferencia, n)
      const resultados = await Promise.all(
        meses.map(async (mes) => {
          const params = new URLSearchParams({ cliente_id: selectedCliente.id, competencia1: mes })
          const res = await fetch(`/api/gestao/kpis/dashboard-resultados?${params}`)
          if (res.ok) return await res.json()
          return null
        })
      )
      const validos = resultados.filter(Boolean)
      if (validos.length === 0) { setDadosPeriodo(null); setLoadingDados(false); return }

      const base = validos[0]
      const indicadoresMedia = base.indicadores.map((ind: any) => {
        const soma = validos.reduce((acc: number, r: any) => {
          const match = r.indicadores.find((i: any) => i.id === ind.id || i.nome === ind.nome)
          return acc + (parseFloat(match?.valor1) || 0)
        }, 0)
        return { ...ind, valor1: (soma / validos.length).toString() }
      })

      const dadosMedia = { ...base, indicadores: indicadoresMedia }
      setDadosPeriodo(dadosMedia)
      setKpisOverride(extrairKPIsDosPeriodo(indicadoresMedia))
      const label = `${formatarMesLabel(meses[0])}\u2013${formatarMesLabel(meses[meses.length - 1])}`
      setDescricaoBase(`M\u00e9dia ${n}M: ${label}`)
    } catch (error) {
      console.error('Erro ao buscar m\u00e9dia de meses:', error)
    }
    setLoadingDados(false)
  }

  const fetchMelhorMes12 = async () => {
    if (!selectedCliente || !periodoReferencia) return
    setLoadingDados(true)
    try {
      const meses = calcularMesesAnteriores(periodoReferencia, 12)
      const resultados = await Promise.all(
        meses.map(async (mes) => {
          const params = new URLSearchParams({ cliente_id: selectedCliente.id, competencia1: mes })
          const res = await fetch(`/api/gestao/kpis/dashboard-resultados?${params}`)
          if (res.ok) { const data = await res.json(); return { mes, data } }
          return null
        })
      )
      const validos = resultados.filter(Boolean) as { mes: string; data: any }[]
      if (validos.length === 0) { setDadosPeriodo(null); setLoadingDados(false); return }

      const getFaturamento = (r: { mes: string; data: any }) => {
        const ind = r.data.indicadores?.find((i: any) => i.nome.toLowerCase().includes('total faturado'))
        return parseFloat(ind?.valor1) || 0
      }
      const melhor = validos.reduce((best, cur) => getFaturamento(cur) > getFaturamento(best) ? cur : best, validos[0])

      setDadosPeriodo(melhor.data)
      setKpisOverride(extrairKPIsDosPeriodo(melhor.data.indicadores || []))
      setDescricaoBase(`Melhor M\u00eas (12M): ${formatarMesLabel(melhor.mes)}`)
    } catch (error) {
      console.error('Erro ao buscar melhor m\u00eas:', error)
    }
    setLoadingDados(false)
  }

  const fetchDadosModoAtivo = async () => {
    setDadosPeriodo(null)
    if (modoBase === 'mes') {
      setKpisOverride(null)
      const [ano, mes] = periodoReferencia.split('-')
      const nomes = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
      setDescricaoBase(`${nomes[parseInt(mes) - 1]}. de ${ano}`)
      await fetchDadosPeriodo()
    } else if (modoBase === 'media3') {
      await fetchDadosMediaMeses(3)
    } else if (modoBase === 'media6') {
      await fetchDadosMediaMeses(6)
    } else if (modoBase === 'melhor12') {
      await fetchMelhorMes12()
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('.cliente-search-container')) {
        setShowClientesList(false)
      }
    }

    if (showClientesList) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showClientesList])

  const fetchClientes = async () => {
    try {
      const res = await fetch('/api/gestao/clientes')
      if (res.ok) {
        const response = await res.json()
        const clientesData = response.data || response
        setClientes(Array.isArray(clientesData) ? clientesData : [])
      }
    } catch (error) {
      console.error('Erro ao buscar clientes:', error)
    }
    setLoadingClientes(false)
  }

  const fetchHistorico = async () => {
    if (!selectedCliente || !user) return

    try {
      const params = new URLSearchParams({
        cliente_id: selectedCliente.id,
        usuario_id: user.id
      })

      const res = await fetch(`/api/gestao/kpis/ia-previsibilidade-historico?${params}`)
      if (res.ok) {
        const data = await res.json()
        setHistorico(data)
      }
    } catch (error) {
      console.error('Erro ao buscar histórico:', error)
    }
  }

  const handleSelectCliente = async (cliente: any) => {
    setSelectedCliente(cliente)
    setShowClientesList(false)
    setSearchTerm('')
    novaConversa()
  }

  const filteredClientes = clientes.filter((cliente: any) => {
    const searchLower = searchTerm.toLowerCase()
    return (
      cliente.nome_empresa?.toLowerCase().includes(searchLower) ||
      cliente.razao_social?.toLowerCase().includes(searchLower) ||
      cliente.nome_completo?.toLowerCase().includes(searchLower)
    )
  })

  const carregarConversa = async (conversaId: string) => {
    if (!user) return

    try {
      const params = new URLSearchParams({
        conversa_id: conversaId,
        usuario_id: user.id
      })

      const res = await fetch(`/api/gestao/kpis/ia-previsibilidade-historico?${params}`)
      if (res.ok) {
        const data = await res.json()
        setMensagens(data.mensagens || [])
        setConversaAtual(conversaId)
        setPeriodoReferencia(data.periodo_referencia)
        setShowHistorico(false)
      }
    } catch (error) {
      console.error('Erro ao carregar conversa:', error)
    }
  }

  const novaConversa = () => {
    setMensagens([])
    setConversaAtual(null)
    setInputMensagem('')
  }

  const deletarConversa = async (conversaId: string) => {
    if (!user || !confirm('Deseja realmente deletar esta conversa?')) return

    try {
      const params = new URLSearchParams({
        conversa_id: conversaId,
        usuario_id: user.id
      })

      const res = await fetch(`/api/gestao/kpis/ia-previsibilidade-historico?${params}`, {
        method: 'DELETE'
      })

      if (res.ok) {
        setHistorico(historico.filter(h => h.id !== conversaId))
        if (conversaAtual === conversaId) {
          novaConversa()
        }
      }
    } catch (error) {
      console.error('Erro ao deletar conversa:', error)
    }
  }

  const abrirModalPlanejamento = (dados: any) => {
    // Normalizar estrutura de dados para sempre ter 'simulacao'
    let dadosNormalizados = dados
    
    // Se tem 'previsao', mover para 'simulacao'
    if (dados.previsao && !dados.simulacao) {
      dadosNormalizados = {
        ...dados,
        simulacao: dados.previsao
      }
    }
    
    // Se tem 'cenarios', usar o realista como simulacao
    if (dados.cenarios && !dados.simulacao) {
      dadosNormalizados = {
        ...dados,
        simulacao: dados.cenarios.realista
      }
    }
    
    setDadosParaSalvar(dadosNormalizados)
    
    // Inicializar valores editáveis
    setValoresEditaveis({
      investimento_simulado: dadosNormalizados.simulacao?.investimento_simulado || 0,
      leads_previstos: dadosNormalizados.simulacao?.leads_previstos || 0,
      agendados_previstos: dadosNormalizados.simulacao?.agendados_previstos || 0,
      comparecidos_previstos: dadosNormalizados.simulacao?.comparecidos_previstos || 0,
      vendas_previstas: dadosNormalizados.simulacao?.vendas_previstas || 0,
      faturamento_previsto: dadosNormalizados.simulacao?.faturamento_previsto || 0,
      roi_previsto: dadosNormalizados.simulacao?.roi_previsto || 0,
      cac_previsto: dadosNormalizados.simulacao?.cac_previsto || 0
    })
    
    // Sugerir próximo mês como período destino
    const hoje = new Date()
    const proximoMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 1)
    const periodoSugerido = `${proximoMes.getFullYear()}-${String(proximoMes.getMonth() + 1).padStart(2, '0')}`
    setPeriodoDestino(periodoSugerido)
    setShowModalPlanejamento(true)
  }

  const salvarNoPlanejamento = async (sobrescrever = false) => {
    if (!selectedCliente || !periodoDestino || !dadosParaSalvar || !user) return

    setSalvandoPlanejamento(true)

    try {
      // Atualizar dadosParaSalvar com valores editados
      const dadosAtualizados = {
        ...dadosParaSalvar,
        simulacao: {
          ...dadosParaSalvar.simulacao,
          ...valoresEditaveis
        }
      }

      const response = await fetch('/api/gestao/kpis/salvar-previsao-planejamento', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cliente_id: selectedCliente.id,
          mes_competencia: periodoDestino + '-01',
          dados_ia: dadosAtualizados,
          sobrescrever,
          usuario_id: user.id
        })
      })

      const data = await response.json()

      if (response.status === 409) {
        // Planejamento já existe
        const confirmar = window.confirm(
          'Já existe um planejamento para este período. Deseja sobrescrever?'
        )
        if (confirmar) {
          await salvarNoPlanejamento(true)
        }
        return
      }

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao salvar planejamento')
      }

      alert(`✅ ${data.mensagem}\n\n📊 ${data.metas_inseridas} metas inseridas\n💰 ${data.investimentos_inseridos} investimentos inseridos`)
      setShowModalPlanejamento(false)
      setDadosParaSalvar(null)
    } catch (error: any) {
      console.error('Erro ao salvar planejamento:', error)
      alert('❌ Erro ao salvar planejamento: ' + error.message)
    } finally {
      setSalvandoPlanejamento(false)
    }
  }

  const enviarMensagem = async () => {
    if (!inputMensagem.trim() || !selectedCliente || !periodoReferencia || !user) return

    const novaMensagem: Mensagem = {
      role: 'user',
      content: inputMensagem,
      timestamp: new Date().toISOString()
    }

    setMensagens(prev => [...prev, novaMensagem])
    setInputMensagem('')
    setLoading(true)

    try {
      const response = await fetch('/api/gestao/kpis/ia-previsibilidade', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cliente_id: selectedCliente.id,
          periodo_referencia: periodoReferencia,
          mensagem_usuario: inputMensagem,
          historico_conversa: mensagens.map(m => ({
            role: m.role,
            content: m.content
          })),
          conversa_id: conversaAtual,
          usuario_id: user.id,
          dados_kpis_override: kpisOverride,
          descricao_base: descricaoBase
        })
      })

      const data = await response.json()

      if (!response.ok) {
        // Mensagem específica para timeout
        if (response.status === 504 || data.tipo === 'timeout') {
          const mensagemErro: Mensagem = {
            role: 'assistant',
            content: '⏱️ A IA está demorando muito para responder.\n\n💡 Sugestões:\n• Tente uma pergunta mais simples\n• Aguarde alguns instantes e tente novamente\n• Divida sua pergunta em partes menores',
            timestamp: new Date().toISOString()
          }
          setMensagens(prev => [...prev, mensagemErro])
          setLoading(false)
          return
        }
        throw new Error(data.error || 'Erro ao processar mensagem')
      }

      const respostaIA: Mensagem = {
        role: 'assistant',
        content: data.resposta,
        dados_calculados: data.dados_calculados,
        timestamp: new Date().toISOString()
      }

      setMensagens(prev => [...prev, respostaIA])
      
      if (data.conversa_id && !conversaAtual) {
        setConversaAtual(data.conversa_id)
        fetchHistorico()
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error)
      alert('Erro ao enviar mensagem: ' + (error as Error).message)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value)
  }

  const renderGraficos = (dados: any) => {
    if (!dados) return null

    if (dados.simulacao) {
      const dadosGrafico = [
        {
          nome: 'Período Base',
          faturamento: dados.dados_base?.faturamento || 0,
          investimento: dados.dados_base?.investimento || 0
        },
        {
          nome: 'Previsão',
          faturamento: dados.simulacao.faturamento_previsto,
          investimento: dados.simulacao.investimento_simulado
        }
      ]

      return (
        <div className="mt-4 space-y-4">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <h4 className="font-semibold mb-3 text-gray-700">📊 Comparação: Base vs Previsão</h4>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={dadosGrafico}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="nome" />
                <YAxis />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="faturamento" fill="#3b82f6" name="Faturamento" />
                <Bar dataKey="investimento" fill="#f59e0b" name="Investimento" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <div className="text-xs text-blue-600 font-semibold mb-1">Faturamento Previsto</div>
              <div className="text-lg font-bold text-blue-900">{formatCurrency(dados.simulacao.faturamento_previsto)}</div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg border border-green-200">
              <div className="text-xs text-green-600 font-semibold mb-1">ROI Previsto</div>
              <div className="text-lg font-bold text-green-900">{dados.simulacao.roi_previsto.toFixed(1)}%</div>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
              <div className="text-xs text-purple-600 font-semibold mb-1">Vendas Previstas</div>
              <div className="text-lg font-bold text-purple-900">{formatNumber(dados.simulacao.vendas_previstas)}</div>
            </div>
            <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
              <div className="text-xs text-orange-600 font-semibold mb-1">CAC Previsto</div>
              <div className="text-lg font-bold text-orange-900">{formatCurrency(dados.simulacao.cac_previsto)}</div>
            </div>
          </div>
        </div>
      )
    }

    if (dados.cenarios) {
      const dadosGrafico = [
        {
          nome: 'Pessimista',
          faturamento: dados.cenarios.pessimista.faturamento_previsto,
          roi: dados.cenarios.pessimista.roi_previsto
        },
        {
          nome: 'Realista',
          faturamento: dados.cenarios.realista.faturamento_previsto,
          roi: dados.cenarios.realista.roi_previsto
        },
        {
          nome: 'Otimista',
          faturamento: dados.cenarios.otimista.faturamento_previsto,
          roi: dados.cenarios.otimista.roi_previsto
        }
      ]

      return (
        <div className="mt-4 bg-white p-4 rounded-lg border border-gray-200">
          <h4 className="font-semibold mb-3 text-gray-700">📈 Comparação de Cenários</h4>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={dadosGrafico}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="nome" />
              <YAxis />
              <Tooltip formatter={(value: number) => formatCurrency(value)} />
              <Legend />
              <Bar dataKey="faturamento" fill="#3b82f6" name="Faturamento" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )
    }

    return null
  }

  return (
    <ProtectedRoute>
      <Layout>
        <div className="p-8 h-screen flex flex-col">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              🤖 Previsibilidade Inteligente
            </h1>
            <p className="text-gray-600 mt-2">
              Converse com a IA para simular cenários e prever faturamento baseado em dados históricos
            </p>
          </div>

          <div className="flex items-center justify-center gap-2 mb-4 px-4">
            <div className="relative cliente-search-container flex-1 max-w-md">
              <input
                type="text"
                placeholder="Buscar cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setShowClientesList(true)}
                className="w-full pl-8 pr-8 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
              />
              <svg className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              {showClientesList && (
                <div className="absolute top-full mt-2 w-full bg-white border-2 border-gray-200 rounded-xl shadow-2xl max-h-96 overflow-y-auto z-50">
                  {filteredClientes.length > 0 ? (
                    <div className="py-2">
                      {filteredClientes.map((cliente: any) => (
                        <button
                          key={cliente.id}
                          onClick={() => handleSelectCliente(cliente)}
                          className="w-full text-left px-6 py-4 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-semibold text-gray-900 text-base">
                            {cliente.nome_empresa || cliente.razao_social}
                          </div>
                          {cliente.nome_completo && (
                            <div className="text-sm text-gray-500 mt-1">{cliente.nome_completo}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="px-6 py-8 text-center text-gray-500">
                      Nenhum cliente encontrado
                    </div>
                  )}
                </div>
              )}
            </div>

            {selectedCliente && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <span className="text-sm font-bold text-blue-900 truncate max-w-xs">
                  {selectedCliente.nome_empresa || selectedCliente.razao_social}
                </span>
                <button
                  onClick={() => {
                    setSelectedCliente(null)
                    novaConversa()
                  }}
                  className="text-blue-600 hover:text-blue-800 transition-colors"
                  title="Remover seleção"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {([
                { key: 'mes', label: 'M\u00eas' },
                { key: 'media3', label: 'M\u00e9dia 3M' },
                { key: 'media6', label: 'M\u00e9dia 6M' },
                { key: 'melhor12', label: 'Melhor 12M' }
              ] as { key: 'mes' | 'media3' | 'media6' | 'melhor12'; label: string }[]).map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setModoBase(opt.key)}
                  className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${
                    modoBase === opt.key
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {modoBase === 'mes' && (
              <input
                type="month"
                value={periodoReferencia}
                onChange={(e) => {
                  setPeriodoReferencia(e.target.value)
                  novaConversa()
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm w-40"
              />
            )}

            {selectedCliente && (
              <button
                onClick={() => setShowHistorico(!showHistorico)}
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1.5"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Histórico
              </button>
            )}
          </div>

          {selectedCliente && (
            <>

              {dadosPeriodo && dadosPeriodo.indicadores && (
                <div className="mb-4 px-4">
                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-3 border border-slate-200 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        Dados do Período Base
                      </h3>
                      <span className="text-xs text-slate-600 bg-white px-2 py-0.5 rounded-full border border-slate-200">
                        {descricaoBase || (() => {
                          const [ano, mes] = periodoReferencia.split('-')
                          const nomes = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
                          return `${nomes[parseInt(mes) - 1]}. de ${ano}`
                        })()}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-2">
                      {(() => {
                        // Indicadores prioritários que devem sempre aparecer
                        const indicadoresPrioritarios = [
                          'total faturado',
                          'investimento em mídias',
                          'leads gerados',
                          'leads agendados',
                          'comparecimento',
                          'total de vendas',
                          'custo por lead',
                          'custo por agendamento',
                          'cac'
                        ]
                        
                        // Filtrar e ordenar indicadores
                        const indicadoresFiltrados = indicadoresPrioritarios
                          .map(nomePrioritario => 
                            dadosPeriodo.indicadores.find((ind: any) => 
                              ind.nome.toLowerCase() === nomePrioritario
                            )
                          )
                          .filter(Boolean) // Remove undefined
                        
                        return indicadoresFiltrados.map((ind: any, idx: number) => {
                          const valor = parseFloat(ind.valor1) || 0
                          const isPercentual = ind.nome.toLowerCase().includes('taxa') || ind.nome.toLowerCase().includes('%')
                          const isCurrency = ind.nome.toLowerCase().includes('faturamento') || 
                                            ind.nome.toLowerCase().includes('investimento') ||
                                            ind.nome.toLowerCase().includes('cac') ||
                                            ind.nome.toLowerCase().includes('ticket') ||
                                            ind.nome.toLowerCase().includes('custo')
                          
                          return (
                            <div key={idx} className="bg-white rounded-md p-2 border border-slate-200 hover:border-blue-300 transition-colors">
                              <div className="text-[10px] font-medium text-slate-600 mb-0.5 truncate" title={ind.nome}>
                                {ind.nome}
                              </div>
                              <div className="text-sm font-bold text-slate-900">
                                {isCurrency ? formatCurrency(valor) : isPercentual ? `${valor.toFixed(1)}%` : formatNumber(valor)}
                              </div>
                            </div>
                          )
                        })
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {selectedCliente && periodoReferencia ? (
            <div className="flex-1 flex gap-4 overflow-hidden">
              {showHistorico && (
                <div className="w-80 bg-white rounded-xl shadow-lg p-4 overflow-y-auto">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-gray-900">Conversas Anteriores</h3>
                    <button
                      onClick={novaConversa}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      + Nova
                    </button>
                  </div>
                  <div className="space-y-2">
                    {historico.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">
                        Nenhuma conversa anterior
                      </p>
                    ) : (
                      historico.map(conv => (
                        <div
                          key={conv.id}
                          className={`p-3 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors ${
                            conversaAtual === conv.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                          }`}
                          onClick={() => carregarConversa(conv.id)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate">
                                {conv.titulo}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(conv.periodo_referencia).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })}
                              </p>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                deletarConversa(conv.id)
                              }}
                              className="ml-2 text-red-500 hover:text-red-700"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              <div className="flex-1 flex flex-col bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  {mensagens.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <div className="text-6xl mb-4">💬</div>
                      <p className="text-lg font-semibold mb-2">
                        Comece uma conversa com a IA!
                      </p>
                      <p className="text-sm mb-4">
                        Exemplos de perguntas:
                      </p>
                      <div className="mt-4 space-y-2 text-left max-w-2xl mx-auto">
                        <div 
                          className="bg-blue-50 p-3 rounded-lg text-sm cursor-pointer hover:bg-blue-100 transition-colors border border-blue-200 hover:border-blue-400"
                          onClick={() => setInputMensagem("Se eu investir R$ 15.000 em mídias, qual será o faturamento previsto?")}
                        >
                          "Se eu investir R$ 15.000 em mídias, qual será o faturamento previsto?"
                        </div>
                        <div 
                          className="bg-blue-50 p-3 rounded-lg text-sm cursor-pointer hover:bg-blue-100 transition-colors border border-blue-200 hover:border-blue-400"
                          onClick={() => setInputMensagem("Qual investimento preciso para atingir R$ 100.000 de faturamento?")}
                        >
                          "Qual investimento preciso para atingir R$ 100.000 de faturamento?"
                        </div>
                        <div 
                          className="bg-blue-50 p-3 rounded-lg text-sm cursor-pointer hover:bg-blue-100 transition-colors border border-blue-200 hover:border-blue-400"
                          onClick={() => setInputMensagem("Compare cenários: otimista, realista e pessimista com investimento de R$ 20.000")}
                        >
                          "Compare cenários: otimista, realista e pessimista com investimento de R$ 20.000"
                        </div>
                        <div 
                          className="bg-blue-50 p-3 rounded-lg text-sm cursor-pointer hover:bg-blue-100 transition-colors border border-blue-200 hover:border-blue-400"
                          onClick={() => setInputMensagem("Se eu dobrar o investimento atual, quanto vou faturar?")}
                        >
                          "Se eu dobrar o investimento atual, quanto vou faturar?"
                        </div>
                        <div 
                          className="bg-blue-50 p-3 rounded-lg text-sm cursor-pointer hover:bg-blue-100 transition-colors border border-blue-200 hover:border-blue-400"
                          onClick={() => setInputMensagem("Qual o investimento necessário para dobrar meu faturamento atual?")}
                        >
                          "Qual o investimento necessário para dobrar meu faturamento atual?"
                        </div>
                        <div 
                          className="bg-blue-50 p-3 rounded-lg text-sm cursor-pointer hover:bg-blue-100 transition-colors border border-blue-200 hover:border-blue-400"
                          onClick={() => setInputMensagem("Quanto preciso investir para gerar 50 vendas?")}
                        >
                          "Quanto preciso investir para gerar 50 vendas?"
                        </div>
                        <div 
                          className="bg-blue-50 p-3 rounded-lg text-sm cursor-pointer hover:bg-blue-100 transition-colors border border-blue-200 hover:border-blue-400"
                          onClick={() => setInputMensagem("Mostre melhor e pior caso com investimento de R$ 12.000")}
                        >
                          "Mostre melhor e pior caso com investimento de R$ 12.000"
                        </div>
                      </div>
                    </div>
                  )}

                  {mensagens.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-3xl px-6 py-4 rounded-2xl ${
                          msg.role === 'user'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-900'
                        }`}
                      >
                        <div className="whitespace-pre-wrap">{msg.content}</div>
                        
                        {msg.dados_calculados && renderGraficos(msg.dados_calculados)}
                        
                        {msg.role === 'assistant' && msg.dados_calculados && (msg.dados_calculados.simulacao || msg.dados_calculados.previsao || msg.dados_calculados.cenarios) && (
                          <div className="mt-4 pt-4 border-t border-gray-300">
                            <button
                              onClick={() => abrirModalPlanejamento(msg.dados_calculados)}
                              className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                              </svg>
                              💾 Salvar como Planejamento
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {loading && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 px-6 py-4 rounded-2xl">
                        <div className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          <span className="text-gray-600">IA está analisando...</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={chatEndRef} />
                </div>

                <div className="border-t border-gray-200 p-4">
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={inputMensagem}
                      onChange={(e) => setInputMensagem(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && enviarMensagem()}
                      placeholder="Digite sua pergunta ou simulação..."
                      className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none"
                      disabled={loading}
                    />
                    <button
                      onClick={enviarMensagem}
                      disabled={loading || !inputMensagem.trim()}
                      className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      Enviar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-gray-50 rounded-xl">
              <div className="text-center text-gray-500">
                <div className="text-6xl mb-4">🎯</div>
                <p className="text-lg font-semibold">
                  Selecione um cliente e período para começar
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Modal de Salvamento no Planejamento */}
        {showModalPlanejamento && dadosParaSalvar && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                    </svg>
                    Salvar como Planejamento
                  </h2>
                  <button
                    onClick={() => setShowModalPlanejamento(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Período de Destino
                  </label>
                  <input
                    type="month"
                    value={periodoDestino}
                    onChange={(e) => setPeriodoDestino(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Selecione o mês para o qual deseja criar o planejamento
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h3 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Editar Valores do Planejamento
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Investimento (R$)</label>
                      <input
                        type="text"
                        value={valoresEditaveis.investimento_simulado.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        onChange={(e) => {
                          const apenasNumeros = e.target.value.replace(/\D/g, '')
                          const valorEmCentavos = parseInt(apenasNumeros) || 0
                          const valorEmReais = valorEmCentavos / 100
                          setValoresEditaveis({...valoresEditaveis, investimento_simulado: valorEmReais})
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                        placeholder="R$ 0,00"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Leads Previstos</label>
                      <input
                        type="text"
                        value={valoresEditaveis.leads_previstos > 0 ? Math.round(valoresEditaveis.leads_previstos).toLocaleString('pt-BR') : ''}
                        onChange={(e) => {
                          const apenasNumeros = e.target.value.replace(/\D/g, '')
                          const valorInteiro = apenasNumeros === '' ? 0 : parseInt(apenasNumeros)
                          setValoresEditaveis({...valoresEditaveis, leads_previstos: valorInteiro})
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Agendados Previstos</label>
                      <input
                        type="text"
                        value={valoresEditaveis.agendados_previstos > 0 ? Math.round(valoresEditaveis.agendados_previstos).toLocaleString('pt-BR') : ''}
                        onChange={(e) => {
                          const apenasNumeros = e.target.value.replace(/\D/g, '')
                          const valorInteiro = apenasNumeros === '' ? 0 : parseInt(apenasNumeros)
                          setValoresEditaveis({...valoresEditaveis, agendados_previstos: valorInteiro})
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Comparecidos Previstos</label>
                      <input
                        type="text"
                        value={valoresEditaveis.comparecidos_previstos > 0 ? Math.round(valoresEditaveis.comparecidos_previstos).toLocaleString('pt-BR') : ''}
                        onChange={(e) => {
                          const apenasNumeros = e.target.value.replace(/\D/g, '')
                          const valorInteiro = apenasNumeros === '' ? 0 : parseInt(apenasNumeros)
                          setValoresEditaveis({...valoresEditaveis, comparecidos_previstos: valorInteiro})
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Vendas Previstas</label>
                      <input
                        type="text"
                        value={valoresEditaveis.vendas_previstas > 0 ? Math.round(valoresEditaveis.vendas_previstas).toLocaleString('pt-BR') : ''}
                        onChange={(e) => {
                          const apenasNumeros = e.target.value.replace(/\D/g, '')
                          const valorInteiro = apenasNumeros === '' ? 0 : parseInt(apenasNumeros)
                          setValoresEditaveis({...valoresEditaveis, vendas_previstas: valorInteiro})
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Faturamento (R$)</label>
                      <input
                        type="text"
                        value={valoresEditaveis.faturamento_previsto.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        onChange={(e) => {
                          const valor = e.target.value.replace(/\D/g, '')
                          setValoresEditaveis({...valoresEditaveis, faturamento_previsto: parseFloat(valor) / 100 || 0})
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">ROI Previsto (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={valoresEditaveis.roi_previsto.toFixed(2)}
                        onChange={(e) => setValoresEditaveis({...valoresEditaveis, roi_previsto: parseFloat(parseFloat(e.target.value).toFixed(2)) || 0})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">CAC Previsto (R$)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={valoresEditaveis.cac_previsto.toFixed(2)}
                        onChange={(e) => setValoresEditaveis({...valoresEditaveis, cac_previsto: parseFloat(parseFloat(e.target.value).toFixed(2)) || 0})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <div className="flex gap-2">
                    <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div className="text-sm text-yellow-800">
                      <p className="font-semibold mb-1">Atenção:</p>
                      <p>Se já existir um planejamento para este período, você será perguntado se deseja sobrescrevê-lo.</p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowModalPlanejamento(false)}
                    className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                    disabled={salvandoPlanejamento}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => salvarNoPlanejamento(false)}
                    disabled={salvandoPlanejamento || !periodoDestino}
                    className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    {salvandoPlanejamento ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Salvando...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Confirmar e Salvar
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Layout>
    </ProtectedRoute>
  )
}
